/**
 * CombatLogService — single source-of-truth for normalized client-side
 * combat events. Subscribes to the existing EventBus emissions
 * (`PLAYER_DAMAGED`, `COMBAT_NPC_DAMAGED`, `WIRE_PLAYER_DIED`,
 * `NPC_DEFEATED`, `ABILITY_CASTED`, `BOSS_PHASE_CHANGED`,
 * `WIRE_PLAYER_RESPAWNED`), resolves sids → display names against the
 * bound Colyseus room, and emits a stream of `CombatLogEvent`s to its
 * own pub/sub.
 *
 * NOTE on wire-vs-local event split: `WIRE_PLAYER_DIED` and
 * `WIRE_PLAYER_RESPAWNED` are deliberately distinct from the existing
 * `PLAYER_DIED` and `PLAYER_RESPAWNED` constants. The wire variants
 * fire ONCE per server broadcast (every player's death/respawn lands
 * here). The local-lifecycle variants fire only for the LOCAL player's
 * own cycle and are consumed by `GameStateManager` + UI overlays. The
 * combat log wants the wire variants — it cares about EVERYONE'S
 * deaths, not just yours.
 *
 * Why a service-shaped indirection between EventBus and consumers:
 *   - Name resolution happens ONCE per wire event, not per consumer
 *     (chat bridge, future killfeed, future damage meter). Without the
 *     service, every consumer would duplicate the room.state.players
 *     lookup + actor-classify logic.
 *   - Damage event coalescing happens ONCE in the service. WoW-style:
 *     identical (source, target, ability) tuples within 500ms collapse
 *     into a single `kind: 'damage'` event with `multiplier: N`. Heal
 *     events coalesce on the same key shape; cast / kill / phase /
 *     respawn / npc_death events DON'T coalesce (they're sparse + each
 *     one is significant).
 *   - The EventBus surface stays general-purpose; consumers that just
 *     want a single combat event type subscribe HERE.
 *
 * Lifecycle:
 *   - `getInstance()` returns the singleton
 *   - `attachRoom(room)` — call when the Colyseus room is bound
 *   - `start()` — wires EventBus subscriptions; idempotent
 *   - `subscribe(handler)` → unsubscribe
 *   - `detach()` — flushes pending coalesced events, clears EventBus
 *     subs, clears room ref. Safe to call without prior attach.
 */

import { EventBus, EVENTS, type Unsubscribe } from '../../../shared/EventBus';
import type { DamageEvent } from '../../../shared/network/DamageEvent';
import type {
  PlayerDiedEvent,
  PlayerRespawnedEvent,
  NpcDefeatedEvent,
  AbilityCastedEvent,
  BossPhaseChangeEvent,
} from '../../../shared/types/NetworkMessages';
import {
  classifyActor,
  type CombatLogEvent,
  type CombatLogDamageEvent,
} from '../../../shared/combat-log/CombatLogEvent';
import { createCoalescer, type Coalescer } from '../../../shared/utils/coalesce';
import { getAbilityData } from '../../../shared/content/generated/abilities.gen';
import type { AbilitySchool } from '../../../shared/constants/CombatFeedbackConfig';
import { gameLogger } from '../../../utils/GameLogger';
import { environmentSourceName } from '../../../shared/combat-log/environmentSource';

/** Damage-event coalesce window in ms. WoW combat log uses ~500ms-ish for
 *  rapid auto-attack collapse; same default here. Tunable via
 *  `setCoalesceWindowMs()` for tests. */
const DEFAULT_COALESCE_WINDOW_MS = 500;

/** Minimal shape of the bound Colyseus room state we read for names
 *  + post-damage HP. The schema fields match Player and NPC schemas
 *  (both declare `health` / `maxHealth` numbers). */
interface NamedRecord {
  heroName?: string;
  name?: string;
  health?: number;
  maxHealth?: number;
}
interface AttachedRoom {
  state: {
    players?: { get?: (id: string) => NamedRecord | undefined };
    npcs?:    { get?: (id: string) => NamedRecord | undefined };
  };
  sessionId?: string;
}

type CombatLogListener = (event: CombatLogEvent) => void;

export class CombatLogService {
  private static instance: CombatLogService | null = null;

  private room: AttachedRoom | null = null;
  private listeners = new Set<CombatLogListener>();
  private busUnsubs: Unsubscribe[] = [];
  private started = false;
  private coalesceWindowMs = DEFAULT_COALESCE_WINDOW_MS;
  private damageCoalescer: Coalescer<CombatLogDamageEvent> | null = null;
  private healCoalescer: Coalescer<any> | null = null;

  private constructor() { /* singleton */ }

  static getInstance(): CombatLogService {
    if (!CombatLogService.instance) {
      CombatLogService.instance = new CombatLogService();
    }
    return CombatLogService.instance;
  }

  /** Reset singleton — for tests only. */
  static resetInstance(): void {
    CombatLogService.instance?.detach();
    CombatLogService.instance = null;
  }

  /** Bind to a live Colyseus room. Re-callable; replaces prior attach. */
  attachRoom(room: AttachedRoom): void {
    this.room = room;
  }

  /** Override the coalesce window (default 500ms). Tests use this. */
  setCoalesceWindowMs(ms: number): void {
    this.coalesceWindowMs = ms;
    // Recreate coalescer with new window so subsequent pushes use it.
    if (this.damageCoalescer) {
      this.damageCoalescer.flush();
      this.damageCoalescer = this.makeDamageCoalescer();
    }
  }

  /** Subscribe a listener. Returns unsubscribe. */
  subscribe(listener: CombatLogListener): Unsubscribe {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  /** Wire EventBus subscriptions. Idempotent. */
  start(): void {
    if (this.started) return;
    this.started = true;

    this.damageCoalescer = this.makeDamageCoalescer();

    const bus = EventBus.getInstance();
    // Phase C.1 (2026-05-12) — single subscription to EVENTS.DAMAGE_APPLIED,
    // dispatched by targetKind. The buildDamageEvent normalizer accepts
    // both raw broadcast shapes (`targetId` for players, `npcId` for NPCs);
    // DamageEvent unifies on `targetId` so we forward it with the
    // appropriate space classifier ('players' | 'npcs').
    this.busUnsubs.push(
      bus.on(EVENTS.DAMAGE_APPLIED, (ev: DamageEvent) => {
        if (ev.targetKind === 'player') this.onDamageEvent(ev, 'players');
        else this.onDamageEvent(ev, 'npcs');
      }),
      bus.on(EVENTS.WIRE_PLAYER_DIED, (msg: any) => this.onPlayerDied(msg)),
      bus.on(EVENTS.NPC_DEFEATED, (msg: any) => this.onNpcDefeated(msg)),
      bus.on(EVENTS.ABILITY_CASTED, (msg: any) => this.onAbilityCasted(msg)),
      bus.on(EVENTS.BOSS_PHASE_CHANGED, (msg: any) => this.onBossPhaseChange(msg)),
      bus.on(EVENTS.WIRE_PLAYER_RESPAWNED, (msg: any) => this.onPlayerRespawned(msg)),
    );
  }

  /** Detach EventBus subs, flush pending coalesced events, clear state. */
  detach(): void {
    for (const unsub of this.busUnsubs) unsub();
    this.busUnsubs = [];
    this.damageCoalescer?.flush();
    this.damageCoalescer = null;
    this.healCoalescer?.flush();
    this.healCoalescer = null;
    this.started = false;
    this.room = null;
  }

  /** Viewer (your own sid) for downstream first-person formatting. */
  viewerSid(): string | null {
    return this.room?.sessionId ?? null;
  }

  // ─── Wire normalizers ────────────────────────────────────────

  /**
   * Phase C.1 (2026-05-12) — entry point fed by DamageSyncSystem via
   * EVENTS.DAMAGE_APPLIED. Replaces the two old onPlayerDamaged /
   * onNpcDamaged handlers; buildDamageEvent treats DamageEvent as a
   * generic raw-msg shape (targetId is on both raw broadcast types so
   * the existing normalizer keeps working).
   */
  private onDamageEvent(ev: DamageEvent, space: 'players' | 'npcs'): void {
    const evt = this.buildDamageEvent(ev, space);
    if (!evt) return;
    this.damageCoalescer?.push(evt);
  }

  private onPlayerDied(msg: PlayerDiedEvent): void {
    if (!msg || typeof msg !== 'object') return;
    // Belt+suspenders for the legacy `victimId` field that some older
    // server builds emitted before the rename to `victimSid`. Modern
    // server only sends `victimSid` (per PlayerDiedEvent type).
    const victimId = msg.victimSid ?? (msg as unknown as { victimId?: string }).victimId ?? null;
    if (!victimId) return;
    const killerId = msg.killerSid ?? null;
    this.emit({
      kind: 'kill',
      ts: Date.now(),
      multiplier: 1,
      sourceId: killerId,
      // A zone kill (spikes, hazards) names the zone type, not its raw `zone:<type>:<id>` tag.
      sourceName: environmentSourceName(killerId) ?? environmentSourceName(msg.killerName)
        ?? msg.killerName ?? this.lookupPlayerName(killerId) ?? 'Environment',
      sourceKind: classifyActor(killerId, 'players'),
      targetId: victimId,
      targetName: msg.victimName ?? this.lookupPlayerName(victimId) ?? '???',
      targetKind: classifyActor(victimId, 'players'),
      isPvP: true,
    });
  }

  private onNpcDefeated(msg: NpcDefeatedEvent): void {
    if (!msg || typeof msg !== 'object' || !msg.npcId) return;
    const byId = msg.defeatedBy ?? null;
    this.emit({
      kind: 'npc_death',
      ts: Date.now(),
      multiplier: 1,
      npcId: String(msg.npcId),
      npcName: msg.npcName ?? this.lookupNpcName(msg.npcId) ?? '???',
      defeatedById: byId,
      defeatedByName: byId ? (this.lookupPlayerName(byId) ?? null) : null,
      defeatedByKind: classifyActor(byId, 'players'),
      xpGained: typeof msg.xpGained === 'number' ? msg.xpGained : 0,
    });
  }

  private onAbilityCasted(msg: AbilityCastedEvent): void {
    if (!msg || typeof msg !== 'object' || !msg.ability_id) return;
    const sourceId = msg.playerId ?? null;
    if (!sourceId) return;
    const targetId = msg.target_id ?? null;
    this.emit({
      kind: 'cast',
      ts: Date.now(),
      multiplier: 1,
      sourceId: String(sourceId),
      sourceName: this.lookupPlayerName(sourceId) ?? '???',
      sourceKind: classifyActor(sourceId, 'players'),
      ability: String(msg.ability_id),
      abilityName: msg.ability_name ?? this.lookupAbilityName(msg.ability_id) ?? msg.ability_id,
      targetId: targetId ? String(targetId) : null,
      targetName: targetId ? (this.lookupPlayerName(targetId) ?? this.lookupNpcName(targetId)) : null,
    });
  }

  private onBossPhaseChange(msg: BossPhaseChangeEvent): void {
    if (!msg || typeof msg !== 'object' || !msg.npcId) return;
    this.emit({
      kind: 'phase',
      ts: Date.now(),
      multiplier: 1,
      npcId: String(msg.npcId),
      npcName: msg.npcName ?? this.lookupNpcName(msg.npcId) ?? '???',
      phaseName: msg.phaseName ?? 'Phase ?',
      hpThreshold: typeof msg.hpThreshold === 'number' ? msg.hpThreshold : 0,
    });
  }

  private onPlayerRespawned(msg: PlayerRespawnedEvent): void {
    if (!msg || typeof msg !== 'object') return;
    // Same legacy-field fallback as onPlayerDied — server now sends
    // `victimSid`; `playerId` is from a pre-rename wire format.
    const targetId = msg.victimSid ?? (msg as unknown as { playerId?: string }).playerId ?? null;
    if (!targetId) return;
    this.emit({
      kind: 'respawn',
      ts: Date.now(),
      multiplier: 1,
      targetId: String(targetId),
      targetName: this.lookupPlayerName(targetId) ?? '???',
      targetKind: classifyActor(targetId, 'players'),
    });
  }

  // ─── Damage shared builder ────────────────────────────────────

  /**
   * Build a damage event from a wire payload. `playerDamaged` and
   * `npcDamaged` share the same shape; only the targetSpace differs.
   */
  private buildDamageEvent(
    msg: any,
    targetSpace: 'players' | 'npcs',
  ): CombatLogDamageEvent | null {
    const sourceId = msg.attackerId ?? null;
    const targetId = msg.targetId ?? msg.npcId ?? null;
    if (!targetId) return null;
    const ability = msg.abilityId ?? null;
    const school: AbilitySchool = (msg.damageSchool ?? 'physical') as AbilitySchool;

    // Source-name + kind resolution: try player schema first, fall
    // back to NPC schema. Mob → player attacks (e.g. pig auto-attacks
    // the mage on /abilitytest) carry the mob id in attackerId, NOT
    // a player session id, so player-only lookup yielded "[???]".
    let sourceName = environmentSourceName(sourceId) ?? '???';
    let sourceKind = classifyActor(sourceId, 'players');
    if (sourceId && sourceName === '???') {
      const playerName = this.lookupPlayerName(sourceId);
      if (playerName) {
        sourceName = playerName;
      } else {
        const npcName = this.lookupNpcName(sourceId);
        if (npcName) {
          sourceName = npcName;
          sourceKind = 'npc';
        }
      }
    }

    // remainingHp / maxHp now come from the live Colyseus schema instead
    // of the broadcast — the playerDamaged / npcDamaged messages no
    // longer carry health/maxHealth (B1 broadcast slim). Stale fallbacks
    // (typeof msg.health === 'number') are kept as belt-and-suspenders
    // for tests that emit the legacy shape directly.
    const hp = this.lookupHealth(targetId, targetSpace);

    return {
      kind: 'damage',
      ts: Date.now(),
      multiplier: 1,
      sourceId: sourceId ? String(sourceId) : '',
      sourceName,
      sourceKind,
      targetId: String(targetId),
      targetName:
        targetSpace === 'players'
          ? (this.lookupPlayerName(targetId) ?? '???')
          : (this.lookupNpcName(targetId) ?? '???'),
      targetKind: classifyActor(targetId, targetSpace),
      ability: ability ? String(ability) : null,
      abilityName: ability ? (this.lookupAbilityName(ability) ?? null) : null,
      school,
      amount: typeof msg.damage === 'number' ? msg.damage : 0,
      critical: !!msg.critical,
      remainingHp: hp?.health ?? (typeof msg.health === 'number' ? msg.health : 0),
      maxHp: hp?.maxHealth ?? (typeof msg.maxHealth === 'number' ? msg.maxHealth : 0),
    };
  }

  private makeDamageCoalescer(): Coalescer<CombatLogDamageEvent> {
    return createCoalescer<CombatLogDamageEvent>({
      keyOf: (e) => `${e.sourceId}|${e.targetId}|${e.ability ?? '_auto'}|${e.critical ? 'C' : 'N'}`,
      windowMs: this.coalesceWindowMs,
      emit: (sample, count) => {
        this.emit({ ...sample, multiplier: count });
      },
    });
  }

  // ─── Lookup helpers ───────────────────────────────────────────

  private lookupPlayerName(id: string | null | undefined): string | null {
    if (!id || !this.room) return null;
    const rec = this.room.state.players?.get?.(String(id));
    return rec?.heroName ?? rec?.name ?? null;
  }

  private lookupNpcName(id: string | null | undefined): string | null {
    if (!id || !this.room) return null;
    const rec = this.room.state.npcs?.get?.(String(id));
    return rec?.name ?? rec?.heroName ?? null;
  }

  /**
   * Read post-damage HP / maxHP for a target straight from the
   * Colyseus schema. Used by buildDamageEvent — was reading msg.health
   * / msg.maxHealth from the broadcast, but those were dropped from
   * playerDamaged / npcDamaged in the B1 broadcast slim (server commit
   * "perf(combat): slim …Damaged broadcasts"). Schema is authoritative
   * and delta-synced; lag vs. broadcast arrival is at most one patch
   * window (~16ms at 60Hz patch rate) — invisible in combat-log UX.
   */
  private lookupHealth(
    id: string | null | undefined,
    space: 'players' | 'npcs',
  ): { health: number; maxHealth: number } | null {
    if (!id || !this.room) return null;
    const rec = space === 'players'
      ? this.room.state.players?.get?.(String(id))
      : this.room.state.npcs?.get?.(String(id));
    if (!rec) return null;
    return {
      health: typeof rec.health === 'number' ? rec.health : 0,
      maxHealth: typeof rec.maxHealth === 'number' ? rec.maxHealth : 0,
    };
  }

  private lookupAbilityName(abilityId: string): string | null {
    const data = getAbilityData(abilityId);
    return data?.name ?? null;
  }

  private emit(event: CombatLogEvent): void {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch (err) {
        // Don't let one bad subscriber break others.
        gameLogger.warn('combat', '[CombatLogService] listener threw', { err });
      }
    }
  }
}
